// pages/components/settings/PersonalInfo.jsx
import React, { useState, useEffect } from 'react';
import { Camera, Save, Loader2, Check } from 'lucide-react';
import apiRequest from '@/services/requests';

const PersonalInfo = () => {
const [loading, setLoading] = useState(true);
const [saving, setSaving] = useState(false);
const [saved, setSaved] = useState(false);
const [uploadingImage, setUploadingImage] = useState(false);
const [formData, setFormData] = useState({
firstName: '',
lastName: '',
email: '',
gender: '',
dob: '',
country: '',
city: '',
phone_number: '',
profile_image: '',
});
const [countries, setCountries] = useState([]);
const [cities, setCities] = useState([]);


useEffect(() => {
loadData();
}, []);
 
const loadData = async () => {
setLoading(true);
try {
const [personalRes, countriesRes] = await Promise.all([
apiRequest.getPersonalInfo(),
apiRequest.countries()
]);

if (personalRes.success) {
setFormData(personalRes.data);

const selectedCountry = countriesRes?.find(
c => c.name === personalRes.data.country
);

setCities(selectedCountry?.cities || []);

}

if (countriesRes) {
setCountries(countriesRes || []);
}
} catch (error) {
console.error('Error loading data:', error);
}
setLoading(false);
};

const handleInputChange = (e) => {
const { name, value } = e.target;

// If country changes, update cities list
if (name === 'country') {
const selectedCountry = countries.find(c => c.name === value);

setCities(selectedCountry?.cities || []);

// Reset city when country changes
setFormData(prev => ({
...prev,
country: value,
city: ''
}));
} else {
setFormData(prev => ({ ...prev, [name]: value }));
}

setSaved(false);
};


const handleImageUpload = async (e) => {
const file = e.target.files?.[0];
if (!file) return;

setUploadingImage(true);
try {
const res = await apiRequest.uploadProfileImage(file);
if (res.success) {
setFormData(prev => ({ ...prev, profile_image: res.profile_image }));
}
} catch (error) {
console.error('Error uploading image:', error);
alert('Failed to upload image');
}
setUploadingImage(false);
};

const handleSubmit = async (e) => {
e.preventDefault();
setSaving(true);

try {
const res = await apiRequest.updatePersonalInfo(formData);
if (res.success) {
setSaved(true);
setTimeout(() => setSaved(false), 3000);
}
} catch (error) {
console.error('Error saving:', error);
alert('Failed to save changes');
}

setSaving(false);
};

if (loading) {
return (
<div className="flex items-center justify-center py-12">
<Loader2 className="animate-spin text-indigo-600" size={32} />
</div>
);
}

return (
<form onSubmit={handleSubmit} className="space-y-8">
<div>
<h2 className="hidden text-md lg:text-2xl font-bold text-slate-900 mb-6">Personal Information</h2>

{/* Profile Image */}
<div className="mb-8">
<label className="block text-sm font-semibold text-slate-700 mb-3">
Profile Photo
</label>
<div className="flex items-center gap-6">
<div className="relative">
<div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
{formData.profile_image ? (
<img 
src={formData.profile_image} 
alt="Profile" 
className="w-full h-full object-cover"
/>
) : (
<span className="text-3xl font-bold text-indigo-600">
{formData.firstName?.[0]}{formData.lastName?.[0]}
</span>
)}
</div>

<label className="absolute bottom-0 right-0 bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-full shadow-lg cursor-pointer transition-all">
<Camera size={16} />
<input
type="file"
accept="image/*"
onChange={handleImageUpload}
className="hidden"
disabled={uploadingImage}
/>
</label>
</div>

{uploadingImage && (
<p className="text-sm text-slate-600">Uploading...</p>
)}
</div>
</div>

{/* Form Fields */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
First Name *
</label>
<input
type="text"
name="firstName"
value={formData.firstName}
onChange={handleInputChange}
required
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Last Name *
</label>
<input
type="text"
name="lastName"
value={formData.lastName}
onChange={handleInputChange}
required
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Email *
</label>
<input
type="email"
name="email"
value={formData.email}
disabled
className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-slate-50 text-slate-500 cursor-not-allowed"
/>
<p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Phone Number
</label>
<input
type="tel"
name="phone_number"
value={formData.phone_number || ''}
onChange={handleInputChange}
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Gender
</label>
<select
name="gender"
value={formData.gender || ''}
onChange={handleInputChange}
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
>
<option value="">Select Gender</option>
<option value="male">Male</option>
<option value="female">Female</option>
<option value="other">Other</option>
<option value="prefer_not_to_say">Prefer not to say</option>
</select>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Date of Birth
</label>
<input
type="date"
name="dob"
value={formData.dob ? new Date(formData.dob).toISOString().split('T')[0] : ''}
onChange={handleInputChange}
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Country
</label>
<select
name="country"
value={formData.country || ''}
onChange={handleInputChange}
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
>
<option value="">Select Country</option>
{countries.map(country => (
<option key={country.id} value={country.name}>
{country.name}
</option>
))}
</select>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
City
</label>
<select
name="city"
value={formData.city || ''}
onChange={handleInputChange}
disabled={!cities.length}
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all disabled:bg-slate-100"
>
<option value="">Select City</option>
{cities.map(city => (
<option key={city.id} value={city.name}>
{city.name}
</option>
))}
</select>

</div>


</div>
</div>

{/* Save Button */}
<div className="flex justify-end pt-6 border-t border-slate-200">
<button
type="submit"
disabled={saving}
className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-lg shadow-md transition-all"
>
{saving ? (
<>
<Loader2 className="animate-spin" size={20} />
Saving...
</>
) : saved ? (
<>
<Check size={20} />
Saved!
</>
) : (
<>
<Save size={20} />
Save Changes
</>
)}
</button>
</div>
</form>
);
};

export default PersonalInfo;